import { Text, Title } from '@mantine/core'
import type { Reference } from '@medplum/fhirtypes'
import { referenceDisplay, getDisplayString } from '@seen/core/display'
import { TASK_CATEGORIES } from '@seen/core/models/tasks/constants'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { createReference } from '@seen/core/references'
import { formatReference } from '@seen/core/utils'
import type { JSX } from 'react'
import { ResourceCard } from '#src/components/core/ResourceCard.tsx'
import { DateDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { RelativeDateTimeDisplay } from '#src/components/display/RelativeDateTimeDisplay.tsx'
import { ResourceStatus } from '#src/components/display/ResourceStatus.tsx'
import { FhirMRTTable } from '#src/components/fhir-table/FhirMRTTable.tsx'

export function SDRTasks(props: { sdr: SDR }): JSX.Element {
  return (
    <div className="space-y-4">
      <Title order={4}>Tasks</Title>
      <FhirMRTTable
        resourceType="Task"
        filterQuery={{
          'part-of': props.sdr.toFhirResource(),
          code: TASK_CATEGORIES.SDR_ACTION,
          _sort: '-_lastUpdated',
        }}
        columnConfigs={[
          {
            id: 'status',
            headerText: 'Status',
            render: (task) => <ResourceStatus resource={task} />,
            valueType: 'text' as const,
            getSortFilterValue: (task) => task.status ?? '',
            filteringType: 'text',
            enableSorting: true,
          },
          {
            id: 'owner',
            headerText: 'Owner',
            render: (task) =>
              task.owner ? <ResourceCard value={task.owner} /> : null,
            valueType: 'text' as const,
            getSortFilterValue: (task) =>
              formatReference(task.owner as Reference),
            filteringType: 'text',
            enableSorting: true,
          },
          {
            id: 'description',
            headerText: 'Description',
            render: (task) => (
              <Text className="text-wrap">
                {referenceDisplay(createReference(task))}
              </Text>
            ),
            valueType: 'text' as const,
            getSortFilterValue: getDisplayString,
            filteringType: 'text',
            enableSorting: true,
          },
          {
            id: 'due',
            headerText: 'Due',
            render: (task) => (
              <DateDisplay value={task.executionPeriod?.end} element={Text} />
            ),
            valueType: 'date' as const,
            // TODO: We should switch to using Task.restriction.period for due date throughout our codebase according to the FHIR spec.
            getSortFilterValue: (task) =>
              new Date(task.executionPeriod?.end ?? ''),
            filteringType: 'date-range',
            enableSorting: true,
            useAsInitialSort: true,
            sortDescFirst: true,
          },
          {
            id: 'lastUpdated',
            headerText: 'Last Updated',
            render: (task) => (
              <RelativeDateTimeDisplay
                value={task.meta?.lastUpdated}
                element={Text}
              />
            ),
            valueType: 'date' as const,
            getSortFilterValue: (task) =>
              new Date(task.meta?.lastUpdated ?? ''),
            filteringType: 'date-range',
            enableSorting: true,
            sortDescFirst: true,
          },
        ]}
      />
    </div>
  )
}
